import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useExchangeRates } from "@/hooks/useExchangeRateQueries";
import { LoadingSpinner } from "@/components/common/LoadingSpinner";
import { ErrorFallback } from "@/components/common/ErrorFallback";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/common/page-header";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { RefreshCw, History, Edit, Plus } from "lucide-react";
import { PermissionGate } from "@/components/common/PermissionGate";
import { usePermissions } from "@/hooks/usePermissions";
import { ExchangeRateDto } from "@/types/exchangeRate";
import { UpdateExchangeRateDialog } from "@/components/settings/UpdateExchangeRateDialog";
import { CreateExchangeRateDialog } from "@/components/settings/CreateExchangeRateDialog";
import { formatDateTime } from "@/lib/utils";
import { getCurrencyFlag } from "@/lib/currencyUtils";

/**
 * Exchange Rates Admin Page
 *
 * Displays current exchange rates for all supported currencies.
 * Allows admins to update rates and view historical changes.
 *
 * Features:
 * - Auto-refresh every 60 seconds
 * - Manual refresh button
 * - Update rate dialog
 * - View history navigation
 * - Currency flags for visual identification
 * - Version badges for tracking changes
 */
export function ExchangeRatesPage() {
  // Rates are readable by every admin role; changing one is SuperAdmin+BillingAdmin.
  const { hasRole } = usePermissions();
  const canEditRates = hasRole(["SuperAdmin", "BillingAdmin"]);
  const navigate = useNavigate();
  const { data: exchangeRates, isLoading, error, refetch } = useExchangeRates();

  const [selectedRate, setSelectedRate] = useState<ExchangeRateDto | null>(
    null,
  );
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const handleUpdateClick = (rate: ExchangeRateDto) => {
    setSelectedRate(rate);
    setUpdateDialogOpen(true);
  };

  const handleViewHistory = (currency: string) => {
    navigate(`/admin/settings/exchange-rates/${currency}/history`);
  };

  const handleUpdateSuccess = () => {
    refetch();
  };

  const handleCreateSuccess = () => {
    refetch();
  };

  if (error) {
    return <ErrorFallback error={error} onRetry={() => refetch()} />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Exchange Rate Configuration"
        subtitle="Manage currency exchange rates - VND base currency"
        actions={[
          ...(canEditRates && (!exchangeRates || exchangeRates.length === 0)
            ? [
                {
                  label: "Create Exchange Rate",
                  onClick: () => setCreateDialogOpen(true),
                  icon: <Plus className="mr-2 h-4 w-4" />,
                },
              ]
            : []),
          {
            label: "Refresh",
            onClick: () => refetch(),
            variant: "outline" as const,
            icon: <RefreshCw className="mr-2 h-4 w-4" />,
          },
        ]}
      />

      {/* Exchange Rates Table */}
      <Card>
        <CardHeader>
          <CardTitle>Current Exchange Rates</CardTitle>
          <CardDescription>
            All rates are based on VND (Vietnamese Dong) as the base currency.
            Updates are automatically applied system-wide.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingSpinner />
          ) : exchangeRates && exchangeRates.length > 0 ? (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[120px]">Currency</TableHead>
                    <TableHead>Exchange Rate</TableHead>
                    <TableHead>Last Updated</TableHead>
                    <TableHead className="w-[100px]">Version</TableHead>
                    <TableHead className="w-[200px] text-right">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {exchangeRates.map((rate) => (
                    <TableRow key={rate.currency}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="text-2xl" title={rate.currency}>
                            {getCurrencyFlag(rate.currency)}
                          </span>
                          <span className="font-medium">{rate.currency}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-mono text-sm">
                          {rate.displayRate}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-muted-foreground">
                          {rate.updatedAt
                            ? formatDateTime(rate.updatedAt)
                            : formatDateTime(rate.effectiveFrom)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="font-mono">
                          v{rate.version}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <PermissionGate roles={["SuperAdmin", "BillingAdmin"]}>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleUpdateClick(rate)}
                            >
                              <Edit className="mr-1 h-3 w-3" />
                              Update
                            </Button>
                          </PermissionGate>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewHistory(rate.currency)}
                          >
                            <History className="mr-1 h-3 w-3" />
                            History
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center space-y-4">
              <p className="text-muted-foreground">
                No exchange rates configured
              </p>
              <p className="text-sm text-muted-foreground">
                Create your first exchange rate to get started
              </p>
              <PermissionGate roles={["SuperAdmin", "BillingAdmin"]}>
                <Button onClick={() => setCreateDialogOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Create Exchange Rate
                </Button>
              </PermissionGate>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Auto-refresh indicator */}
      <div className="flex items-center justify-center">
        <p className="text-xs text-muted-foreground">
          Auto-refreshes every 60 seconds
        </p>
      </div>

      {/* Update Exchange Rate Dialog */}
      {selectedRate && (
        <UpdateExchangeRateDialog
          rate={selectedRate}
          open={updateDialogOpen}
          onClose={() => {
            setUpdateDialogOpen(false);
            setSelectedRate(null);
          }}
          onSuccess={handleUpdateSuccess}
        />
      )}

      {/* Create Exchange Rate Dialog */}
      <CreateExchangeRateDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={handleCreateSuccess}
      />
    </div>
  );
}
